// formatApiError — toma un error de axios y devuelve un mensaje en español
// usable en la UI (toast, alert, label).
//
// Uso tipico:
//   try {
//     await api.post(...);
//   } catch (err) {
//     setError(formatApiError(err, 'No se pudo guardar'));
//   }
//
// Prioridad:
//   1. Errores de red (sin response) → mensaje de conectividad
//   2. Status HTTP conocido (401, 403, 404, ...) → mensaje especifico
//   3. detail del backend → lo muestra tal cual
//   4. fallback → el texto que pasaste como segundo argumento

export function formatApiError(err, fallback = 'Error inesperado. Intenta de nuevo.') {
  // Sin response = error de red o timeout
  if (!err?.response) {
    if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
      return 'La operación tardó demasiado. Revisa tu conexión e intenta de nuevo.';
    }
    if (err?.message === 'Network Error' || !navigator.onLine) {
      return 'Sin conexión al servidor. Revisa tu internet.';
    }
    return fallback;
  }

  const { status, data } = err.response;

  // El backend expone detalles via `detail` (FastAPI) o `message` (otros)
  // Si detail viene como array (Pydantic validation errors), tomamos el primero
  let backendDetail = data?.detail ?? data?.message;
  if (Array.isArray(backendDetail) && backendDetail.length > 0) {
    const first = backendDetail[0];
    backendDetail = typeof first === 'string' ? first : (first?.msg || JSON.stringify(first));
  }
  if (typeof backendDetail !== 'string') {
    backendDetail = null;
  }

  // Mapeo por status code
  switch (status) {
    case 400:
      return backendDetail || 'Datos inválidos.';
    case 401:
      return 'Credenciales incorrectas o sesión expirada.';
    case 403:
      return backendDetail || 'No tienes permiso para esta acción.';
    case 404:
      return backendDetail || 'Recurso no encontrado.';
    case 409:
      return backendDetail || 'Conflicto con datos existentes.';
    case 413:
      return 'Archivo demasiado grande.';
    case 422:
      return backendDetail || 'Los datos no pasan validación.';
    case 429:
      return 'Demasiadas peticiones. Espera un momento y reintenta.';
    default:
      break;
  }

  if (status >= 500) {
    return backendDetail
      ? `Error del servidor: ${backendDetail}`
      : 'Error del servidor. Intenta de nuevo o contacta soporte.';
  }

  return backendDetail || fallback;
}
